<template>
    <div class="history-modal-parameter-component">
        <div class="parameter-card-header">
            <div class="profile-info">
                <div class="picture">
                    <img
                        v-if="item.userPhotoUrl || item.photo"
                        :src="item.userPhotoUrl || item.photo"
                        alt="User photo"
                    />
                    <AvatarPlaceholder
                        v-else
                        size="38px"
                        :text="picturePlaceholder"
                    />
                </div>
                <div class="user-meta">
                    <div class="name">
                        {{ `${item.firstName || ""} ${item.lastName || ""}`.trim() || "-" }}
                    </div>
                    <div class="sub-meta">
                        <span class="appointment-id">ID: <strong>{{ item.id }}</strong></span>
                        <template v-if="item.turnoName">
                            <span class="meta-separator">•</span>
                            <span class="shift">{{ item.turnoName }}</span>
                        </template>
                        <template v-if="item.resource || item.resourceName">
                            <span class="meta-separator">•</span>
                            <span class="resource-name">{{ item.resource || item.resourceName }}</span>
                        </template>
                    </div>
                </div>
            </div>

            <div class="header-right">
                <div class="date-badge">
                    <span class="date-text">{{ formatDate(item.eventTime) }}</span>
                </div>
                <div
                    v-if="item.statusName"
                    class="status-pill"
                    :style="statusStyle"
                >
                    {{ item.statusName }}
                </div>
            </div>
        </div>

        <div class="parameter-card-body">
            <div class="params-header-title">
                <span class="title-text">{{ $t("Operator.CustomParameters") }}</span>
                <span class="params-count">({{ item.parameters ? item.parameters.length : 0 }})</span>
            </div>

            <div class="params-grid">
                <div
                    v-for="(param, pIdx) in item.parameters"
                    :key="pIdx"
                    class="param-item"
                >
                    <div class="param-label-row">
                        <span class="param-label">{{ param.parameterLabel || param.parameterName }}</span>
                        <span
                            v-if="param.parameterLabel && param.parameterName && param.parameterLabel !== param.parameterName"
                            class="param-tech-name"
                        >
                            ({{ param.parameterName }})
                        </span>
                    </div>
                    <div class="param-value-container">
                        <span class="param-value">{{ param.displayValue || param.rawValue }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { avatarText } from "@core/utils/filter";
import AvatarPlaceholder from "@/@core/components/avatar-placeholder/index.vue";
import moment from "moment";

export default {
    name: "ItemParameter",
    components: {
        AvatarPlaceholder
    },
    props: {
        item: {
            type: Object,
            required: true,
            default: () => ({})
        }
    },
    computed: {
        picturePlaceholder() {
            return avatarText(this.item.firstName, this.item.lastName);
        },
        statusStyle() {
            const color = this.item.statusColor || "#974900";
            return {
                backgroundColor: `${color}18`,
                color: color,
                borderColor: `${color}40`
            };
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return "-";
            const language = this.$cookies.get("userInfo")?.language;
            if (language === "pt-BR") {
                return moment(date).format("DD/MM/YYYY HH:mm");
            }
            return moment(date).format("MM/DD/YYYY HH:mm");
        }
    }
};
</script>

<style lang="scss" scoped>
.history-modal-parameter-component {
    border-radius: 16px;
    border: 1px solid #cfc4be;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .parameter-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 24px;
        background-color: #faf9f8;
        border-bottom: 1px solid #f0eeec;
        flex-wrap: wrap;
        gap: 12px;

        .profile-info {
            display: flex;
            align-items: center;
            gap: 12px;

            .picture {
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                    width: 38px;
                    height: 38px;
                    object-fit: cover;
                    border-radius: 50%;
                    border: 2px solid #ffffff;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
            }

            .user-meta {
                display: flex;
                flex-direction: column;

                .name {
                    color: #4c4541;
                    font-size: 16px;
                    font-weight: 600;
                    line-height: 22px;
                }

                .sub-meta {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 13px;
                    color: #998f8a;

                    .appointment-id {
                        strong {
                            color: #4c4541;
                        }
                    }

                    .meta-separator {
                        color: #cfc4be;
                    }

                    .shift {
                        color: #7b736e;
                    }
                }
            }
        }

        .header-right {
            display: flex;
            align-items: center;
            gap: 10px;

            .date-badge {
                font-size: 14px;
                font-weight: 500;
                color: #4c4541;
                background-color: #ffffff;
                padding: 4px 10px;
                border-radius: 6px;
                border: 1px solid #e2deda;
            }

            .status-pill {
                display: inline-flex;
                align-items: center;
                padding: 4px 12px;
                border-radius: 12px;
                font-size: 13px;
                font-weight: 600;
                border: 1px solid transparent;
                white-space: nowrap;
            }
        }
    }

    .parameter-card-body {
        padding: 16px 24px 20px;

        .params-header-title {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 12px;

            .title-text {
                font-size: 13px;
                font-weight: 700;
                color: #998f8a;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .params-count {
                font-size: 12px;
                font-weight: 600;
                color: #974900;
            }
        }

        .params-grid {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .param-item {
                display: flex;
                flex-direction: column;
                background-color: #fafafa;
                border: 1px solid #ece8e5;
                border-radius: 8px;
                padding: 10px 14px;
                transition: border-color 0.15s ease;

                &:hover {
                    border-color: #ffd8b8;
                }

                .param-label-row {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 4px;

                    .param-label {
                        font-size: 14px;
                        font-weight: 600;
                        color: #974900;
                    }

                    .param-tech-name {
                        font-size: 12px;
                        color: #b0a8a3;
                    }
                }

                .param-value-container {
                    .param-value {
                        font-size: 15px;
                        line-height: 22px;
                        color: #4c4541;
                        font-weight: 500;
                        white-space: pre-wrap;
                        word-break: break-word;
                    }
                }
            }
        }
    }
}

@media (max-width: 576px) {
    .history-modal-parameter-component {
        .parameter-card-header {
            flex-direction: column;
            align-items: flex-start;

            .header-right {
                width: 100%;
                justify-content: space-between;
            }
        }
    }
}
</style>

